import React, { useMemo } from "react";
import { Card, Empty, Typography } from "antd";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useThemeStyles } from "../../../context/ThemeContext";
import { formatUsd } from "../utils";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const { Text } = Typography;

export default function GroupBudgetUtilizationChart({ groups = [], days = 30, loading = false }) {
  const themeStyles = useThemeStyles();

  const chartData = useMemo(() => {
    const labels = groups.map((g) => g.group_name || g.group_id || "Group");
    const spend = groups.map((g) => Number(g.spend_usd || 0));
    const budget = groups.map((g) => {
      const lim = g?.budget?.monthly_limit_usd;
      return lim == null ? 0 : Number(lim);
    });
    return {
      labels,
      datasets: [
        {
          label: `Spend (${days}d)`,
          data: spend,
          backgroundColor: "rgba(148, 22, 127, 0.75)",
          borderRadius: 4,
          maxBarThickness: 36,
        },
        {
          label: "Group budget",
          data: budget,
          backgroundColor: "rgba(99, 102, 241, 0.55)",
          borderRadius: 4,
          maxBarThickness: 36,
        },
      ],
    };
  }, [groups, days]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: { color: themeStyles.textPrimary },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${formatUsd(ctx.parsed.y)}`,
          },
        },
      },
      scales: {
        x: {
          ticks: { color: themeStyles.textSecondary, maxRotation: 45, minRotation: 0 },
          grid: { color: themeStyles.borderColor },
        },
        y: {
          beginAtZero: true,
          ticks: {
            color: themeStyles.textSecondary,
            callback: (v) => `$${v}`,
          },
          grid: { color: themeStyles.borderColor },
        },
      },
    }),
    [themeStyles],
  );

  const hasData = groups.length > 0;

  return (
    <Card
      loading={loading}
      title="Group budget utilization"
      style={{ marginBottom: 16, background: themeStyles.cardBg }}
    >
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        Compare group spend against the budget cap you assigned. Expand a group in the table below to see
        project-level utilization (managed by group admins).
      </Text>
      {hasData ? (
        <div style={{ height: 280 }}>
          <Bar data={chartData} options={options} />
        </div>
      ) : (
        <Empty description="No groups to display" />
      )}
    </Card>
  );
}
